import {
  CustomTableCell,
  CustomTableHead,
  Dialog,
  FormLabel,
  FormSectionHeader,
  Loader,
  Select,
} from "@/components";
import useAccessConfig, { PERMISSION_MATRIX } from "./useAccessConfig";
import {
  Button,
  Checkbox,
  Input,
  Table,
  TableBody,
  TableHeader,
  TableRow,
} from "@/components/ui";
import React from "react";
import { Bookmark, ChevronRight } from "lucide-react";
import {
  cn,
  permissionMatrixTitles,
  permissionModules,
  permissionRoles,
  transformActions,
  transformPermissions,
} from "@/lib/utils";

const randomColors = [
  { bg: "bg-blue-100", stroke: "stroke-blue-600" },
  { bg: "bg-green-100", stroke: "stroke-green-600" },
  { bg: "bg-violet-100", stroke: "stroke-violet-600" },
  { bg: "bg-amber-100", stroke: "stroke-amber-600" },
  { bg: "bg-teal-100", stroke: "stroke-teal-600" },
];

{/* Note: Do not modify this component directly.
  To add or remove permissions, update permissionMatrixTitles in src/lib/utils.tsx
  — all other settings will update automatically.  */}

const AccessConfig = () => {
  const {
    communityList,
    communityId,
    onCommunityChange,
    isCommunityLoading,
    communityData,
    communityKey,
    isPermissionLoading,
    isBOMPositionsLoading,
    onCheckboxChange,
    onSave,
    isUpdatePermissionPending,
    showSaveConfirm,
    setShowSaveConfirm,
    onSaveConfirm,
    sortedBomPositions,
    form,
    onChangeModuleText,
    perModules,
    searchModuleText,
    onModuleChange,
    selectedModule,
    selectedModuleName,
    totalActivities,
    onSelectAll,
    allSelectAllState,
    onReset,
  } = useAccessConfig();

  return (
    <div className="bg-white p-4 md:p-6 rounded-md">
      {isCommunityLoading || isPermissionLoading || isBOMPositionsLoading ? (
        <Loader />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] mb-10">
            <h2 className="text-lg font-[600] items-start flex">
              {communityData?.name}
            </h2>
            {/* Right side: select + bookmark */}
            <div className="flex items-end justify-end gap-5">
              <div className="w-full md:w-2/3">
                <FormLabel>Select Community</FormLabel>
                <Select
                  key={communityKey}
                  placeholder=""
                  searchPlaceholder="Search.."
                  noDataPlaceholder="Community Not Found"
                  options={communityList?.data?.map((d: any) => ({
                    value: d.communityId,
                    label: d.name,
                  }))}
                  value={communityId}
                  onChange={onCommunityChange}
                  className="h-[36px] w-full"
                />
              </div>

              <div className="flex items-center gap-4 self-center shrink-0">
                <button type="button" onClick={onSave}>
                  {isUpdatePermissionPending ? (
                    <Loader variant="secondary" />
                  ) : (
                    <Bookmark className="h-6 w-6" />
                  )}
                </button>
                <label className="flex items-center gap-2 text-sm whitespace-nowrap cursor-pointer">
                  <Checkbox
                    checked={allSelectAllState}
                    onCheckedChange={(checked: boolean) => onSelectAll(!!checked)}
                    aria-label="Select all"
                  />
                  Select All
                </label>
                <Button type="button" variant="outline" onClick={onReset}>
                  Reset
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-10 gap-3">
            <div className="md:col-span-1 lg:col-span-2 flex flex-col gap-2 border border-gray-200 rounded-md p-3">
              <FormSectionHeader wrapperClassName="mb-0">Modules</FormSectionHeader>
              <Input
                onChange={onChangeModuleText}
                placeholder="Search module"
                value={searchModuleText}
              />
              <div className="flex flex-col gap-1 my-3 h-[40vh] overflow-y-scroll">
                {perModules?.map((module: any, index: number) => {
                  const Icon = module.icon;
                  const colorIndex = index % 5;
                  const isModuleSelected = module.key == selectedModule;
                  const activities = totalActivities(module.key);
                  return (
                    <div key={`per-module-${index}`}
                      className={cn("flex flex-row items-center justify-between cursor-pointer hover:bg-blue-50 p-2 rounded-md", isModuleSelected ? "bg-blue-50" : "")}
                      onClick={() => onModuleChange(module.key)}
                    >
                      <div className="flex flex-row gap-2">
                        <div className={`${randomColors[colorIndex].bg} h-8 w-8 flex items-center justify-center rounded-sm`}>
                          <Icon className={`h-5 w-5 ${randomColors[colorIndex].stroke}`} />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[14px] font-medium">{module.label}</span>
                          <span className="text-[12px] text-gray-400">{activities} Activities</span>
                        </div>
                      </div>
                      {isModuleSelected ? <ChevronRight className="h-5 w-5 stroke-3 stroke-primary"/> : <></>}
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="md:col-span-2 lg:col-span-8 border border-gray-200 rounded-md p-3">
              <FormSectionHeader wrapperClassName="mb-2" className="flex flex-col">
                <span className="mb-2">{selectedModuleName} <span className="px-2 py-1 text-[10px] text-primary bg-blue-50 rounded-full">{totalActivities(selectedModule)} activities</span></span>
                <span className="text-[12px] text-gray-400">Configure permission for {selectedModuleName} module activities</span>
              </FormSectionHeader>
              <Table className="min-w-[1000px] overflow-x-auto">
                <TableHeader className="w-max table-fixed border-collapse bg-gray-100 mb-3">
                  <TableRow className="rounded table w-full table-fixed">
                    <CustomTableHead className="sticky top-0 !w-[50px] text-left bg-transparent text-black border-r-0">
                      Activity
                    </CustomTableHead>
                    <CustomTableHead className="sticky top-0 !w-[50px] bg-transparent text-black border-r-0">
                      CAM
                    </CustomTableHead>
                    <CustomTableHead className="sticky top-0 !w-[50px] bg-transparent text-black border-r-0">
                      CAM-A
                    </CustomTableHead>
                    {sortedBomPositions?.length ? (
                      sortedBomPositions?.map((position: any, positionIndex: number) => (
                        <CustomTableHead
                          key={position?.bomPositionId || positionIndex}
                          className="sticky top-0 !w-[50px] bg-transparent text-black border-r-0 whitespace-normal break-words"
                        >
                          {position.positionName}
                        </CustomTableHead>
                      ))
                    ) : (
                      <></>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody className="block w-max max-h-[calc(100vh-500px)] overflow-y-scroll w-full">
                  {form.watch(PERMISSION_MATRIX) ? (
                    Object.entries(
                      transformActions(
                        transformPermissions(form.watch(PERMISSION_MATRIX))
                      )
                    )
                    ?.sort(([a], [b]) => a.toLowerCase().localeCompare(b.toLowerCase()))
                    ?.map(([moduleName, actions], index) => {
                      const selectedModuleChildren = permissionModules?.find((module: any) => module.key?.toLowerCase() == selectedModule?.toLowerCase())?.children || [];
                      if (!selectedModuleChildren?.includes(moduleName)) return;
                      
                      const labels =
                        permissionMatrixTitles[
                          moduleName as keyof typeof permissionMatrixTitles
                        ];

                      return (
                        <React.Fragment key={`module-${index}`}>
                          {/* <TableRow
                            key={`row-${index}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            className="text-primary bg-blue-50 text-[14px] font-semibold col-span-7 border-blue-50 hover:bg-blue-50 table w-full table-fixed"
                          >
                            <CustomTableCell className="py-3">
                              {moduleName
                                .toLowerCase()
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (c) => c.toUpperCase())}
                            </CustomTableCell>
                            <CustomTableCell></CustomTableCell>
                            <CustomTableCell></CustomTableCell>
                            {sortedBomPositions?.length ? (
                              sortedBomPositions?.map((_: any) => (
                                <CustomTableCell></CustomTableCell>
                              ))
                            ) : (
                              <></>
                            )}
                          </TableRow> */}
                          {labels?.map((perm: any, idx: any) => (
                            <TableRow
                              key={`action-${idx}`}
                              className="table w-full table-fixed"
                            >
                              <CustomTableCell className="py-3 sticky left-0 z-10">
                                <span className="block max-w-[140px] whitespace-normal break-words" title={perm.label}>
                                  {perm.label}
                                </span>
                              </CustomTableCell>
                              {Array.from({
                                length: sortedBomPositions?.length + 2,
                              }).map((_, i) => {
                                const currentAction = Object.entries(
                                  actions as any
                                ).find(([action, _data]) => perm.value == action);
                                let permissionObj = "";

                                if (i == 0) {
                                  permissionObj = (
                                    currentAction?.[1] as any
                                  )?.find(
                                    (val: any) =>
                                      val.permissionScope == "USER_ROLE" &&
                                      val.userRole == "ROLE_CAM" &&
                                      val.actions.includes(
                                        currentAction?.[0] as any
                                      )
                                  ) as any;
                                } else if (i == 1) {
                                  permissionObj = (
                                    currentAction?.[1] as any
                                  )?.find(
                                    (val: any) =>
                                      val.permissionScope == "USER_ROLE" &&
                                      val.userRole == "ROLE_CAM_ACCOUNTANT" &&
                                      val.actions.includes(
                                        currentAction?.[0] as any
                                      )
                                  ) as any;
                                } else {
                                  permissionObj = (
                                    currentAction?.[1] as any
                                  )?.find((val: any) => {
                                    return (
                                      val.permissionScope == "BOM_POSITION" &&
                                      val.bomPositionId ==
                                        sortedBomPositions?.[i - 2]
                                          ?.bomPositionId &&
                                      val.actions.includes(
                                        currentAction?.[0] as any
                                      )
                                    );
                                  }) as any;
                                }

                                const columnRoleType =
                                  i === 0
                                    ? permissionRoles.ROLE_CAM
                                    : i === 1
                                    ? permissionRoles.ROLE_CAM_ACCOUNTANT
                                    : permissionRoles.BOM_POSITION;

                                const isDisabled =
                                  Array.isArray((perm as any)?.disabledRoles) &&
                                  (perm as any).disabledRoles.includes(columnRoleType);

                                return (
                                  <CustomTableCell key={i}>
                                    <div className="flex items-center justify-center">
                                      <Checkbox
                                        disabled={isDisabled}
                                        checked={isDisabled ? false : !!permissionObj}
                                        onCheckedChange={(checked: boolean) => {
                                          if (isDisabled) return;
                                          onCheckboxChange({
                                            obj: permissionObj
                                              ? {
                                                  ...(permissionObj as any),
                                                  module: moduleName,
                                                }
                                              : null,
                                            checked,
                                            action:
                                              currentAction?.[0] || perm.value,
                                            isRemoved: !checked,
                                            defaultObj:
                                              i == 0 || i == 1
                                                ? {
                                                    permissionScope: "USER_ROLE",
                                                    userRole:
                                                      i == 0
                                                        ? "ROLE_CAM"
                                                        : "ROLE_CAM_ACCOUNTANT",
                                                    module: moduleName,
                                                  }
                                                : {
                                                    bomPositionId:
                                                      sortedBomPositions?.[i - 2]
                                                        ?.bomPositionId,
                                                    permissionScope:
                                                      "BOM_POSITION",
                                                    module: moduleName,
                                                  },
                                          });
                                        }}
                                      />
                                    </div>
                                  </CustomTableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <></>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
      <Dialog
        open={showSaveConfirm}
        setOpen={setShowSaveConfirm}
        title={
          <div className="text-2xl font-bold text-primary">
            {"Confirmation"}
            <div className="h-1 w-[30%] bg-primary rounded-full" />
          </div>
        }
        saveText="Update"
        isLoading={isUpdatePermissionPending}
        onSave={onSaveConfirm}
      >
        <p className="text-sm">
          Are you sure you want to update permission for {communityData?.name}?
        </p>
      </Dialog>
    </div>
  );
};

export default AccessConfig;
